/**
 * SaaS Admin — dashboard nicho rico (KPIs reales por business profile).
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { apiGetJson } from "@/lib/sm-api";

type TenantOption = { id: string; name: string; slug: string };

type NichePayload = {
  tenantId: string;
  tenantName: string;
  slug: string;
  planId: string;
  businessProfile: string;
  period: string;
  range: { start: string; end: string; days: number };
  niche: {
    title: string;
    blurb: string;
    focusMetrics: Array<{ id: string; label: string; enabled: boolean; reason: string }>;
  };
  kpis: Array<{ id: string; label: string; value: string | number; sub?: string }>;
};

export function AdminNicheAnalyticsDashboard({
  tenants,
  tenantId: controlledTenantId,
}: {
  tenants?: TenantOption[];
  tenantId?: string;
}) {
  const [localTenants, setLocalTenants] = useState<TenantOption[]>(tenants ?? []);
  const [tenantId, setTenantId] = useState(controlledTenantId ?? "");
  const [period, setPeriod] = useState<"7d" | "30d" | "90d">("30d");
  const [data, setData] = useState<NichePayload | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (tenants) {
      setLocalTenants(tenants);
      return;
    }
    apiGetJson<TenantOption[] | { tenants: TenantOption[] }>("/api/admin/tenants")
      .then((r) => {
        const list = Array.isArray(r) ? r : (r as { tenants: TenantOption[] }).tenants ?? [];
        setLocalTenants(list.map((t: TenantOption) => ({ id: t.id, name: t.name, slug: t.slug })));
      })
      .catch((e) => toast.error(e instanceof Error ? e.message : "Error tenants"));
  }, [tenants]);

  useEffect(() => {
    if (controlledTenantId) setTenantId(controlledTenantId);
  }, [controlledTenantId]);

  const load = useCallback(async (id: string, p: string) => {
    if (!id) return;
    setLoading(true);
    try {
      const r = await apiGetJson<NichePayload>(
        `/api/admin/niche-analytics?tenantId=${encodeURIComponent(id)}&period=${encodeURIComponent(p)}`,
      );
      setData(r);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error al cargar nicho");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tenantId) void load(tenantId, period);
  }, [tenantId, period, load]);

  return (
    <div className="space-y-4 rounded-lg border border-border bg-card/40 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-foreground">Dashboard nicho</h3>
          <p className="text-sm text-muted-foreground">
            KPIs efectivos según <code>businessProfile</code> del tenant (no solo gobernanza de flags).
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {!controlledTenantId ? (
            <Select value={tenantId || undefined} onValueChange={setTenantId}>
              <SelectTrigger className="w-[240px]">
                <SelectValue placeholder="Elegir tenant" />
              </SelectTrigger>
              <SelectContent>
                {localTenants.map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.name} ({t.slug})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : null}
          <Select value={period} onValueChange={(v) => setPeriod(v as "7d" | "30d" | "90d")}>
            <SelectTrigger className="w-[110px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7d">7d</SelectItem>
              <SelectItem value="30d">30d</SelectItem>
              <SelectItem value="90d">90d</SelectItem>
            </SelectContent>
          </Select>
          <Button
            size="sm"
            variant="outline"
            disabled={!tenantId || loading}
            onClick={() => void load(tenantId, period)}
          >
            Refrescar
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Cargando KPIs…
        </div>
      ) : null}

      {data && !loading ? (
        <div className="space-y-3">
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 text-sm">
            <span className="font-medium text-foreground">{data.niche.title}</span>
            <span className="text-muted-foreground">
              {data.tenantName} · plan {data.planId} · {data.range.start} → {data.range.end}
            </span>
          </div>
          <p className="text-xs text-muted-foreground">{data.niche.blurb}</p>

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {data.kpis.map((k) => (
              <div
                key={k.id}
                className="rounded-md border border-border bg-background/80 px-3 py-2.5"
              >
                <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{k.label}</p>
                <p className="mt-1 text-lg font-semibold tabular-nums text-foreground">{k.value}</p>
                {k.sub ? <p className="text-xs text-muted-foreground">{k.sub}</p> : null}
              </div>
            ))}
          </div>

          {data.niche.focusMetrics.length > 0 ? (
            <div>
              <p className="mb-1 text-xs font-medium text-muted-foreground">Métricas habilitadas del foco nicho</p>
              <ul className="flex flex-wrap gap-1.5">
                {data.niche.focusMetrics.map((m) => (
                  <li
                    key={m.id}
                    className="rounded-full border border-border px-2 py-0.5 text-[11px] text-foreground"
                    title={m.reason}
                  >
                    {m.label}
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <p className="text-xs text-amber-700">
              Ninguna métrica del foco nicho está habilitada (plan / flag / override).
            </p>
          )}
        </div>
      ) : null}
    </div>
  );
}
